import React, { useEffect, useState, useCallback } from "react";
import { motion } from "framer-motion";
import { ArrowsClockwise, Lock, Baseball, Basketball, PaperPlaneTilt } from "@phosphor-icons/react";
import { toast } from "sonner";
import api from "../lib/api";
import TeamLogo from "./TeamLogo";

const fmtTime = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleTimeString(undefined, {
      hour: "numeric", minute: "2-digit", timeZoneName: "short",
    });
  } catch { return "—"; }
};

const fmtMoney = (n) => {
  if (n == null) return "—";
  const v = Number(n);
  const sign = v > 0 ? "+" : v < 0 ? "−" : "";
  return `${sign}$${Math.abs(v).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
};

function SlateRow({ pick, sport, onPush, pushing }) {
  const analyst = pick.analyst || "—";
  const accent = analyst.toLowerCase() === "charlie" ? "#007AFF" : "#A855F7";
  const alreadySent = !!pick.impromptu_sent_at;
  return (
    <tr className="border-b border-white/5" data-testid={`slate-row-${sport}-${pick.team_abbr}`}>
      <td className="px-3 py-2.5">
        <span className="text-[10px] uppercase tracking-widest font-bold" style={{ color: accent }}>
          {analyst}
        </span>
      </td>
      <td className="px-2 py-2.5">
        <div className="flex items-center gap-2">
          <TeamLogo sport={sport} abbr={pick.team_abbr} size={22} />
          <span className="font-bold text-sm">{pick.team_abbr}</span>
          <span className="text-zinc-500 text-xs">{pick.is_home ? "vs" : "@"}</span>
          <TeamLogo sport={sport} abbr={pick.opponent_abbr} size={18} />
          <span className="text-xs text-zinc-400">{pick.opponent_abbr}</span>
        </div>
      </td>
      <td className="px-2 py-2.5 font-mono-d text-xs text-zinc-400">{fmtTime(pick.game_time_utc)}</td>
      <td className="px-2 py-2.5 font-mono-d text-xs text-zinc-300">
        {pick.moneyline_used != null ? (pick.moneyline_used > 0 ? `+${pick.moneyline_used}` : pick.moneyline_used) : "—"}
      </td>
      <td className="px-2 py-2.5 font-mono-d text-xs text-zinc-300">
        {pick.stake != null ? `$${Number(pick.stake).toFixed(0)}` : "—"}
      </td>
      <td className="px-2 py-2.5 font-mono-d text-xs text-[#22C55E]">{fmtMoney(pick.potential_profit)}</td>
      <td className="px-3 py-2.5 text-[10px] text-zinc-500 italic max-w-[220px] truncate" title={pick.recommendation}>
        {pick.recommendation || "—"}
      </td>
      <td className="px-3 py-2.5 text-right">
        <button
          onClick={() => onPush(pick)}
          disabled={pushing || !pick.lock_id}
          data-testid={`slate-push-${sport}-${pick.team_abbr}`}
          title={alreadySent ? `Already blasted at ${new Date(pick.impromptu_sent_at).toLocaleTimeString()} — click to resend` : `Send impromptu email blast for this ${sport.toUpperCase()} pick`}
          className={`inline-flex items-center gap-1 px-2 py-1 text-[10px] uppercase tracking-widest font-bold rounded-sm border transition-colors disabled:opacity-40 disabled:cursor-not-allowed ${
            alreadySent
              ? "border-zinc-500 text-zinc-300 hover:border-zinc-300 hover:text-white"
              : "border-[#22C55E]/40 text-[#22C55E] hover:bg-[#22C55E]/15"
          }`}
        >
          <PaperPlaneTilt size={11} weight="fill" />
          {alreadySent ? "Resend" : "Push now"}
        </button>
      </td>
    </tr>
  );
}

function SportSlate({ sport, picks, asOf, onPush, pushing }) {
  const Icon = sport === "mlb" ? Baseball : Basketball;
  const accent = sport === "mlb" ? "#007AFF" : "#FF6B35";
  return (
    <div className="bg-[#121212] border border-white/10 rounded-sm overflow-x-auto" data-testid={`slate-${sport}`}>
      <div className="px-4 py-3 border-b border-white/10 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Icon size={16} weight="fill" style={{ color: accent }} />
          <h4 className="font-display text-lg">{sport.toUpperCase()} · TODAY</h4>
          <span className="text-[10px] uppercase tracking-widest text-zinc-500 ml-1">{picks.length} locked</span>
        </div>
        {asOf && (
          <span className="text-[10px] text-zinc-500 font-mono-d">as of {new Date(asOf).toLocaleTimeString()}</span>
        )}
      </div>
      {picks.length === 0 ? (
        <div className="px-4 py-8 text-center text-xs text-zinc-500 italic">
          No {sport.toUpperCase()} picks locked for today. Shortlist a team that currently triggers under Charlie's or Dakota's strategy to add one.
        </div>
      ) : (
        <table className="w-full text-sm min-w-[860px]">
          <thead>
            <tr className="text-[10px] uppercase tracking-widest text-zinc-500 border-b border-white/5">
              <th className="text-left px-3 py-2">Analyst</th>
              <th className="text-left px-2 py-2">Matchup</th>
              <th className="text-left px-2 py-2">Game time</th>
              <th className="text-left px-2 py-2">ML</th>
              <th className="text-left px-2 py-2">Stake</th>
              <th className="text-left px-2 py-2">To win</th>
              <th className="text-left px-3 py-2">Reason</th>
              <th className="text-right px-3 py-2">Push</th>
            </tr>
          </thead>
          <tbody>
            {picks.map((p) => <SlateRow key={p.lock_id || `${p.team_abbr}-${p.analyst}`} pick={p} sport={sport} onPush={onPush} pushing={pushing} />)}
          </tbody>
        </table>
      )}
    </div>
  );
}

export default function TodaysSlatePanel() {
  const [mlb, setMlb] = useState([]);
  const [wnba, setWnba] = useState([]);
  const [asOf, setAsOf] = useState(null);
  const [loading, setLoading] = useState(false);
  const [pushing, setPushing] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      // Use locked-debug for admin since it returns the full slate including
      // settled picks for context. Filter to today client-side.
      const [{ data: mlbData }, { data: wnbaData }] = await Promise.all([
        api.get("/admin/picks/locked-debug"),
        api.get("/wnba/picks/feed"),
      ]);
      const today = new Date().toISOString().slice(0, 10);
      const mlbToday = (mlbData?.picks || []).filter((p) => p.target_date === today);
      // WNBA uses public feed since there's no admin locked-debug for wnba.
      // upcoming = today's pending picks already; settled = past picks.
      const wnbaUpcoming = (wnbaData?.upcoming || []).filter((p) => p.target_date === today);
      setMlb(mlbToday);
      setWnba(wnbaUpcoming);
      setAsOf(new Date().toISOString());
    } catch (e) {
      // eslint-disable-next-line no-console
      console.warn("Today's slate load failed", e);
    } finally {
      setLoading(false);
    }
  }, []);

  const pushPick = useCallback(async (pick, sport) => {
    if (!pick.lock_id) {
      toast.error("Missing lock_id — can't blast this pick.");
      return;
    }
    const label = `${(pick.analyst || "").toString()} · ${pick.team_abbr} ${pick.is_home ? "vs" : "@"} ${pick.opponent_abbr}`;
    const already = !!pick.impromptu_sent_at;
    const msg = already
      ? `Resend impromptu email for ${label}? Subscribers will receive a second alert.`
      : `Send impromptu email for ${label} to Sports subscribers now?`;
    if (!window.confirm(msg)) return;
    setPushing(true);
    try {
      const { data } = await api.post("/admin/picks/impromptu-send", {
        pick_id: pick.lock_id,
        product: sport,
        audience: "active",
      });
      toast.success(`Sent to ${data.sent || 0} subscriber(s)${data.skipped ? ` · ${data.skipped} skipped` : ""}`);
      await load();  // refresh so impromptu_sent_at flips and button label updates
    } catch (e) {
      toast.error(e.response?.data?.detail || "Push failed");
    } finally {
      setPushing(false);
    }
  }, [load]);

  useEffect(() => { load(); }, [load]);

  return (
    <motion.section
      initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }}
      className="mt-10 pt-8 border-t border-white/10"
      data-testid="todays-slate-panel"
    >
      <div className="flex items-end justify-between flex-wrap gap-3 mb-4">
        <div>
          <div className="text-[11px] uppercase tracking-[0.3em] text-[#22C55E] mb-1">Locked · Today</div>
          <h3 className="font-display text-3xl sm:text-4xl leading-none">TODAY'S SLATE</h3>
          <p className="text-xs text-zinc-500 mt-2 max-w-2xl">
            Live view of what's currently in <span className="font-mono-d text-zinc-300">daily_picks_locked</span> for today.
            Shortlist changes auto-trigger a re-lock, so picks added by your shortlist edits should appear here within seconds.
          </p>
        </div>
        <button
          onClick={load}
          disabled={loading}
          data-testid="slate-refresh"
          className="inline-flex items-center gap-1.5 px-3 py-2 border border-white/15 hover:border-white/40 text-xs uppercase tracking-widest rounded-sm transition-colors disabled:opacity-40"
        >
          <ArrowsClockwise size={12} className={loading ? "animate-spin" : ""} />
          {loading ? "Loading" : "Refresh"}
        </button>
      </div>
      <div className="grid grid-cols-1 gap-4">
        <SportSlate sport="mlb" picks={mlb} asOf={asOf} onPush={(p) => pushPick(p, "mlb")} pushing={pushing} />
        <SportSlate sport="wnba" picks={wnba} asOf={asOf} onPush={(p) => pushPick(p, "wnba")} pushing={pushing} />
      </div>
    </motion.section>
  );
}
